import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { Loading } from "../../../widgets/Loading";

type Props = {};

interface BlacklistRow {
  faa_manufacturer: string;
  faa_model: string;
}

/**
 * Blacklist tab - shows FAA types that have been ignored (drones, balloons, etc.)
 * Allows restoring blacklisted records back to the main mapping view.
 */
// Common styling for sticky table headers
const headerCellSx = {
  backgroundColor: "background.paper",
  fontWeight: 600,
  borderBottom: 2,
  borderColor: "divider",
};

export const BlacklistTab: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();

  const { loading, value: blacklisted } = useAsync(async () => {
    const { data, error } = await postgrest
      .from("faa_to_airplx")
      .select("faa_manufacturer,faa_model")
      .eq("blacklisted", true)
      .order("faa_manufacturer")
      .order("faa_model");

    if (error) {
      console.error("Failed to fetch blacklisted records:", error);
      return [];
    }

    return (data as BlacklistRow[]) || [];
  }, [postgrest]);

  const [restoredRows, setRestoredRows] = React.useState<Set<string>>(
    new Set()
  );

  const handleRestore = async (item: BlacklistRow) => {
    const key = `${item.faa_manufacturer}|${item.faa_model}`;

    // Optimistic update - hide row immediately
    setRestoredRows((prev) => new Set(prev).add(key));

    try {
      // Upsert for consistency - handles edge cases where row may not exist
      await postgrest.from("faa_to_airplx").upsert(
        {
          faa_manufacturer: item.faa_manufacturer,
          faa_model: item.faa_model,
          blacklisted: false,
        },
        { onConflict: "faa_manufacturer,faa_model" }
      );

      snackbar.notify({
        text: t("screens.blacklistTab.restoredMessage", {
          manufacturer: item.faa_manufacturer,
          model: item.faa_model
        }),
        severity: "success",
      });
    } catch (error: any) {
      // Rollback on error - show row again
      setRestoredRows((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });

      console.error("Failed to restore:", error);
      snackbar.notify({
        text: t("screens.blacklistTab.restoreError"),
        severity: "error",
      });
    }
  };

  // Filter out restored rows for optimistic UI
  const displayedBlacklist = React.useMemo(() => {
    if (!blacklisted) return [];

    return blacklisted.filter((item) => {
      const key = `${item.faa_manufacturer}|${item.faa_model}`;
      return !restoredRows.has(key);
    });
  }, [blacklisted, restoredRows]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Loading />
      </Box>
    );
  }

  if (!displayedBlacklist || displayedBlacklist.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Typography variant="body1" color="text.secondary">
          {t("screens.blacklistTab.emptyState")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>{t("screens.blacklistTab.headers.manufacturer")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.blacklistTab.headers.model")}</TableCell>
            <TableCell align="center" width={100} sx={headerCellSx}>
              {t("screens.blacklistTab.headers.restore")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedBlacklist.map((item) => (
            <TableRow
              key={`${item.faa_manufacturer}|${item.faa_model}`}
              sx={{ "&:hover": { backgroundColor: "action.hover" } }}
            >
              <TableCell>{item.faa_manufacturer}</TableCell>
              <TableCell>{item.faa_model}</TableCell>
              <TableCell align="center">
                <Tooltip title={t("screens.blacklistTab.restoreTooltip")}>
                  <IconButton
                    size="small"
                    onClick={() => handleRestore(item)}
                    color="primary"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
